/**
 * Route copy for `src/routes/communities` (keys: `routes.communities.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  acceptCommunityInvitePage: {
    invalidInvitation: 'Invalid invitation',
    invalidInvitationLinkMissingToken: 'Invalid invitation link (missing token).',
    thisInvitationIsNoLongerValid: 'This invitation is no longer valid.',
    invitationNotFound: 'Invitation not found',
    thisInvitationCannotBeFoundOr: 'This invitation cannot be found or has expired.',
    invitationAcceptedYouAreNowPart: 'Invitation accepted — you are now part of the community.',
    thisInvitationCannotBeFound: 'This invitation cannot be found.',
    invitationExpired: 'Invitation expired',
    thisInvitationHasExpiredOrHas: 'This invitation has expired or has already been used.',
    invitationAlreadyUsed: 'Invitation already used',
    youAreAlreadyAMemberOf: 'You are already a member of this community.',
    couldNotAcceptTheInvitationError: 'Could not accept the invitation (error {{status}}).',
    couldNotAcceptTheInvitationRight: 'Could not accept the invitation right now.',
    loadingYourInvitation: 'Loading your invitation…',
    finalizingYourMembership: 'Finalizing your membership…',
    youReInvitedToAContent: 'You\'re invited to a content community',
    join: 'Join',
    aCommunity: 'a community',
    as: 'as',
    vInvitationSentTo: '({{v}}). Invitation sent to',
    iAlreadyHaveAnAccountSign: 'I already have an account — Sign in',
    createAnAccount: 'Create an account',
    signInOrSignUpWith: 'Sign in or sign up with the address',
    toAcceptYouWillThenBe: 'to accept. You will then be asked for your consent and to connect your accounts, at your own pace.',
    validatingYourInvitation: 'Validating your invitation…',
    viewMyEngagements: 'View my engagements',
    backToTheApp: 'Back to the app',
    invitationAcceptedAndConsentRecorded: 'Invitation accepted and consent recorded — you will receive this community’s suggestions.',
    invitationAcceptedNoSuggestionsUntilConsent: 'Invitation accepted. You will not receive any suggestion until you give your consent (from “My engagements”).',
    consentToRenew: 'Consent needs renewing',
    consentTextChangedReload: 'The consent text has changed — reload the page to read the current version.',
    toAcceptConsentAskedNextStep: 'to accept. Your consent will be asked for explicitly at the next step.',
    joinName: 'Join {{name}}',
    theCommunity: 'the community',
    role: 'Role:',
    acceptTheInvitationAndParticipate: 'Accept the invitation and take part',
    acceptTheInvitation: 'Accept the invitation',
    withoutConsentNoSuggestions: 'Without consent, you join the community but receive no suggestion.',
  },
  communitiesLayout: {
    myEngagements: 'My engagements',
    groupsAdmin: 'Groups (admin)',
    policyCompliance: 'Policy & compliance',
    measure: 'Measure',
    contentCommunitiesAuthenticAmplification: 'Content communities (authentic amplification)',
    oneAccountPostsTheAiCommunity: 'One account posts; the AI Community Manager comments and reacts from the accounts of members who agreed — as a recommendation to approve, or automatically on their behalf for members who enabled it. Always governed by consent, the ToS/Law 25 notice, caps (confidence, daily, quiet hours, anti-burst) and the emergency stop.',
    accountHealth: 'Account health',
  },
  groupsAdminPage: {
    draft: 'Draft',
    active: 'Active',
    paused: 'Paused',
    archived: 'Archived',
    groupUpdated: 'Group updated.',
    groupCreatedDraft: 'Group created (draft).',
    savingFailed: 'Saving failed.',
    editTheGroup: 'Edit the group',
    newGroupDraft: 'New group (draft)',
    name: 'Name',
    description: 'Description',
    cancel: 'Cancel',
    save: 'Save',
    createTheDraft: 'Create the draft',
    connectionsSaved: 'Connections saved.',
    savingTheConnectionsFailed: 'Saving the connections failed.',
    connectionsV: 'Connections — {{v}}',
    forEachPlatformLinkTheAccount: 'For each platform, link the account this member will use to publish (editor) or amplify. Leave empty if the member has not connected their account: their engagements will then switch to a',
    manualPublishingLink: 'manual publishing link',
    noApiAction: '(no API action).',
    connectionReferenceConnectedAccountIdEmp: 'Connection reference (connected account id) — empty = manual link',
    commentReaction: 'comment + reaction',
    commentOnly: 'comment only',
    couldNotLoadTheMembers: 'Could not load the members.',
    invitationCreatedTheMemberMustAccept: 'Invitation created — the member must accept it to take part.',
    theInvitationFailedValidEmail: 'The invitation failed (valid email?).',
    couldNotChangeTheRole: 'Could not change the role.',
    vIsNowTheEditorAccount: '{{v}} is now the editor account.',
    couldNotDesignate: 'Could not designate.',
    memberRemovedConsentRevoked: 'Member removed (consent revoked).',
    couldNotRemove: 'Could not remove.',
    invitationLinkCopied: 'Invitation link copied.',
    couldNotCopySelectTheLink: 'Could not copy — select the link manually.',
    noEditorAccountDesignatedChooseEditor: 'No editor account designated — choose “Editor” on a member (one account posts, the others amplify).',
    memberEmail: 'Member email',
    type: 'Type',
    invite: 'Invite',
    invitationLinkForV: 'Invitation link for {{v}}:',
    copy: 'Copy',
    theMemberOpensThisLinkSigns: 'The member opens this link, signs in or creates an account, then accepts. Their consent (CASL / Law 25) is requested before any engagement.',
    loadingMembers: 'Loading members…',
    noMembersYetInviteAnInternal: 'No members yet. Invite an internal ambassador or an external user.',
    member: 'Member',
    status: 'Status',
    role: 'Role',
    accounts: 'Accounts',
    consent: 'Consent',
    editor: 'Editor',
    vConnected: '{{v}} connected',
    noneManualLink: 'none → manual link',
    remove: 'Remove',
    couldNotLoadTheGroups: 'Could not load the groups.',
    couldNotChangeTheStatus: 'Could not change the status.',
    couldNotArchive: 'Could not archive.',
    amplificationGroups: 'Amplification groups',
    createAGroupDraftFirstInvite: 'Create a group (draft first), invite members, designate the editor account and the amplifiers, then link each account. Nothing is posted automatically.',
    newGroup: 'New group',
    loading: 'Loading…',
    noGroupsCreateOneToOrganize: 'No groups. Create one to organize authentic amplification: one account posts, consenting members comment and react from their own account.',
    hideMembers: 'Hide members',
    manageMembers: 'Manage members',
    edit: 'Edit',
    enable: 'Enable',
    pause: 'Pause',
    archive: 'Archive',
    theMemberGivesTheirOwnConsent: 'The member opens this link, signs in or creates an account, then accepts. They give their consent themselves (CASL / Law 25) — you cannot consent on their behalf. Without consent, no recommendation is sent to them.',
    consented: '✓ Consented',
    consentedOnDate: ' on {{date}}',
    consentWithdrawn: 'Withdrawn',
    consentWithdrawnByMember: 'Withdrawn by the member',
    awaitingTheMember: 'Awaiting the member',
  },
  kit: {
    contentCommunitiesAreNotAvailableOn: 'Content communities are not available on this environment yet.',
    readBeforeEnablingAutomaticEngagement: '⚠️ Read before enabling automatic engagement',
    automated: 'Automated,',
    coordinatedEngagement: 'coordinated engagement',
    commentsReactionsFromSeveralAccountsTo: '(comments / reactions from several accounts to “boost” the algorithm) may',
    breakTheRules: 'break the rules',
    ofMetaFacebookInstagramLinkedinTiktok: 'of Meta (Facebook/Instagram), LinkedIn, TikTok and X, and',
    exposeYourAccount: 'expose your account',
    toRestrictionOrSuspension: 'to restriction or suspension.',
    automaticModeStays: 'Automatic mode stays',
    strictlyPersonal: 'strictly personal',
    itOnlyActsOn: ': it only acts on',
    your: 'your',
    accountWith: 'account, with',
    consentAndNeverInPlaceOf: 'consent, and never in place of another member.',
    itRemainsGovernedByTheGroup: 'It remains governed by the group policy (confidence threshold, daily cap, quiet hours, anti-burst spacing) and by the',
    emergencyStop: 'emergency stop',
    reviews: 'Reviews',
    yourEngagementActivityIsLoggedWithout: ': your engagement activity is logged (without the content) and',
    revocableAtAnyTime: 'revocable at any time',
    byDefaultEveryActionRemainsSubject: '. By default, every action remains subject to your approval.',
    recommendationKeep: 'Recommendation: keep',
    manualApproval: 'manual approval',
    andOnlyEnableAutomaticModeWith: 'and only enable automatic mode with full knowledge of these risks.',
  },
  measurementPage: {
    draftInProgress: 'Draft in progress',
    toApprove: 'To approve',
    approved: 'Approved',
    edited: 'Edited',
    published: 'Published',
    postedAuto: 'Posted (auto)',
    skipped: 'Skipped',
    blocked: 'Blocked',
    manualLink: 'Manual link',
    unknown: 'Unknown',
    firstCreateAGroupInThe: 'First create a group in the “Groups (admin)” tab.',
    group: 'Group',
    measurementUnavailable: 'Measurement unavailable.',
    loading: 'Loading…',
    noMeasurementForThisGroup: 'No measurement for this group.',
    reactions: 'Reactions',
    manualLinks: 'Manual links',
    engagedMembers: 'Engaged members',
    byPlatform: 'By platform',
    postedPostedRecommendedSuggested: ': {{posted}} posted / {{recommended}} suggested',
    byStatus: 'By status',
    noRecommendationRecordedYet: 'No recommendation recorded yet.',
    comments: 'Comments',
  },
  myEngagementsPage: {
    reactionLike: 'like',
    community: 'Community',
    couldNotLoadYourEngagements: 'Could not load your engagements.',
    loading: 'Loading…',
    noRecommendationsYetWhenAnEditor: 'No recommendations yet. When an editor account in your groups posts, a suggested comment will appear here — you will approve (or edit) it before posting from your own account.',
    history: 'History',
    draftInProgress: 'Draft in progress',
    toApprove: 'To approve',
    approved: 'Approved',
    edited: 'Edited',
    published: 'Published',
    postedAuto: 'Posted (auto)',
    skipped: 'Skipped',
    blockedCompliance: 'Blocked (compliance)',
    manualLinkProvided: 'Manual link provided',
    noConnectedAccountOpenTheLink: 'No connected account — open the link to post manually.',
    postedFromYourAccount: 'Posted from your account.',
    actionRefusedReason: 'Action refused ({{reason}}).',
    theActionFailed: 'The action failed.',
    recommendationSkipped: 'Recommendation skipped.',
    commentRemoved: 'Comment removed.',
    couldNotRemoveWindowExpired: 'Could not remove (window expired?).',
    originalPost: 'Original post',
    suggestedCommentEditable: 'Suggested comment (editable)',
    reaction: 'Reaction:',
    notSupportedOnVCommentOnly: 'not supported on {{v}} (comment only)',
    approveEditedAndPost: 'Approve (edited) and post',
    approveAndPost: 'Approve and post',
    dismiss: 'Dismiss',
    reopenTheManualPublishingLink: 'Reopen the manual publishing link',
    removeTheComment: 'Remove the comment',
    reversibleUntil: 'Reversible until',
    consentRecordedYouWillReceive: 'Consent recorded — you will receive this community’s suggestions.',
    couldNotRecordYourConsent: 'Could not record your consent.',
    consentWithdrawnNoMoreSuggestions: 'Consent withdrawn — no more suggestions will be sent to you.',
    withdrawalFailed: 'Withdrawal failed.',
    myCommunitiesAndMyConsent: 'My communities and my consent',
    consentGiven: '✓ Consent given',
    consentGivenOnDate: ' on {{date}}',
    withoutConsentNoSuggestion: 'Without consent — no suggestion',
    withdrawMyConsent: 'Withdraw my consent',
    giveMyConsent: 'Give my consent',
    consentName: 'Consent — {{name}}',
    saveMyConsent: 'Save my consent',
    cancelConsent: 'Cancel',
    thisCommunity: 'this community',
    withdrawConsentConfirm: 'You will no longer receive any suggestion from “{{name}}” and any automatic mode will be turned off. You remain a member and can give your consent again later.',
    withdraw: 'Withdraw',
  },
  policyPage: {
    firstCreateAGroupInThe: 'First create a group in the “Groups (admin)” tab.',
    group: 'Group',
    couldNotLoadThePolicy: 'Could not load the policy.',
    policySaved: 'Policy saved.',
    savingFailed: 'Saving failed.',
    emergencyStopActivated: 'Emergency stop activated.',
    emergencyStopLifted: 'Emergency stop lifted.',
    theActionFailed: 'The action failed.',
    automaticModeDisabledForThisMember: 'Automatic mode disabled for this member.',
    actionNotPossible: 'Action not possible.',
    loading: 'Loading…',
    thisGroupSPolicyIsNot: 'This group\'s policy is not initialized yet.',
    emergencyStopKillSwitch: 'Emergency stop (kill switch)',
    immediatelyBlocksEveryAutomaticEngagemen: 'Immediately blocks every automatic engagement of the group. Recommendations stay visible; only the automatic action is suspended.',
    reEnable: 'Re-enable',
    stopNow: 'Stop now',
    groupAutomaticEngagement: 'Group automatic engagement',
    offByDefaultEvenWhenEnabled: 'Off by default. Even when enabled here, nothing happens until a member has given their individual opt-in below.',
    iAcknowledgeTheRisksRelatedTo: 'I acknowledge the risks related to the platforms\' rules (ToS) and the Law 25 / CASL notice — required to enable automatic engagement.',
    automaticComments: 'Automatic comments',
    automaticReactionsOnlyOnThePlatforms: 'Automatic reactions (only on the platforms allowed below)',
    confidenceThreshold01: 'Confidence threshold (0–1)',
    dailyCapMember: 'Daily cap / member',
    antiBurstSpacingSeconds: 'Anti-burst spacing (seconds)',
    quietHoursStart: 'Quiet hours — start',
    quietHoursEnd: 'Quiet hours — end',
    platformsAllowedForAutoAutonomousMode: 'Platforms allowed for auto (autonomous mode per platform)',
    automaticOptInPerMember: 'Automatic opt-in per member',
    eachMemberDecidesIndividuallyActivationR: 'Each member decides individually. Activation requires the member to have consented and explicitly acknowledge the risks (below); it stays revocable at any time.',
    noMembersInThisGroup: 'No members in this group.',
    autoEnabled: 'Auto enabled',
    manualApprovalDefault: 'Manual approval (default)',
    awaitingConsent: 'Awaiting consent',
    auto: 'Auto ✓',
    manual: 'Manual',
    disableAuto: 'Disable auto',
    theMemberMustConsentFirst: 'The member must consent first',
    enableAuto: 'Enable auto…',
    automaticModeEnabledForThisMember: 'Automatic mode enabled for this member.',
    activationFailed: 'Activation failed.',
    enableAutoV: 'Enable auto — {{v}}',
    cancel: 'Cancel',
    enableKnowingly: 'Enable knowingly',
    iAcknowledgeThatAutomatedEngagementMay: 'I acknowledge that automated engagement may break the platforms\' rules and expose the member\'s account to restriction or suspension.',
    iHaveReadTheLaw25: 'I have read the Law 25 / CASL notice: activity is logged (without the content) and revocable at any time.',
  },
  memberConsentPanel: {
    consentLanguage: 'Consent language',
  },
};

export default en;
